import React, { useState } from 'react';

import { Box, ButtonLink, Popover, Spinner, Text } from 'core/components';
import DeviceLink from 'app/components/links/DeviceLink';
import FilteredDevicesLink from 'app/components/links/FilteredDevicesLink';
import { usePlanDevicePage } from 'app/util/lookups/deviceRange';

const PAGE_SIZE = 20;

interface PlanDevicesPageProps {
  planId: string | number;
  pageIndex: number;
}

// one component per page, as hooks can't be called in a loop
const PlanDevicesPage = ({ planId, pageIndex }: PlanDevicesPageProps) => {
  const { devices, error } = usePlanDevicePage(planId, pageIndex, PAGE_SIZE);

  return (
    <>
      {!!error && (
        <Text as="div" color="danger">
          Unable to load devices.
        </Text>
      )}

      {devices.map((device) => (
        <Box key={device.id} mb="4px">
          <DeviceLink deviceId={device.id} name={device.device_name} />
        </Box>
      ))}
    </>
  );
};

interface PlanDevicesListProps {
  planId: string | number;
  showViewAllLink: boolean;
}

// only mounted while the popover is open, so devices load on demand
const PlanDevicesList = ({ planId, showViewAllLink }: PlanDevicesListProps) => {
  const [pageCount, setPageCount] = useState(1);

  // the same cached pages the page components read, so these add no requests
  const { restricted, isLoading: isLoadingFirstPage } = usePlanDevicePage(planId, 0, PAGE_SIZE);
  const { hasMore, isLoading: isLoadingLastPage } = usePlanDevicePage(planId, pageCount - 1, PAGE_SIZE);

  return (
    <Box p={1} maxHeight={500} overflow="auto" minWidth={180}>
      <Text as="div" mb={1} fontWeight="bold">
        Devices in this plan:
      </Text>

      {showViewAllLink && (
        <Box mb={1}>
          <FilteredDevicesLink filters={{ plan: [`${planId}`], isArchived: false }}>
            <Text>View All</Text>
          </FilteredDevicesLink>
        </Box>
      )}

      {isLoadingFirstPage && <Spinner size={16} />}

      {Array.from({ length: pageCount }, (_, pageIndex) => (
        <PlanDevicesPage key={pageIndex} planId={planId} pageIndex={pageIndex} />
      ))}

      {(hasMore || (pageCount > 1 && isLoadingLastPage)) && (
        <ButtonLink fontSize={14} mt={1} disabled={isLoadingLastPage} onClick={() => setPageCount(pageCount + 1)}>
          {isLoadingLastPage ? 'Loading...' : 'Show More'}
        </ButtonLink>
      )}

      {restricted && (
        <Text as="div" color="muted" mt={1}>
          Only showing devices you have access to view
        </Text>
      )}
    </Box>
  );
};

interface PlanDevicesPopoverProps {
  planId: string | number;
  showViewAllLink?: boolean;
}

const PlanDevicesPopover = ({ planId, showViewAllLink = true }: PlanDevicesPopoverProps) => (
  <Popover
    minimal={false}
    targetTagName="span"
    position="left-top"
    content={<PlanDevicesList planId={planId} showViewAllLink={showViewAllLink} />}
  >
    <Text role="link" as="a" color="primary">
      (Show)
    </Text>
  </Popover>
);

export default PlanDevicesPopover;
